import { Injector } from '@angular/core';
import { DialogService } from '@common-services/ui/dialog.service';
import { LoaderHelper, LoaderService } from '@common-services/ui/loader.service';
import { AclService } from '@common-services/util/acl.service';
import { AuthService } from '@common-services/util/auth.service';
import { EventBusService } from '@common-services/util/event-bus.service';
import { Module, StoreService } from '@common-services/util/store.service';
import { MESSAGETYPE } from '@constants/dialog.const';
import { DIALOG } from '@constants/events.const';
import { Project, StateChangeType, UNAUTHORIZED } from '@constants/router.const';
import { AppModuleInjector } from '@dice/app.module';
import { RouterActions } from '@dice/app/store/router/router.actions';
import { MessageDialogPayload } from '@dice/app/types/dialog.types';
import { NavigationElement } from '@dice/app/types/navigation.types';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { StateObject, Trace, Transition, TransitionService, UIRouter, UrlParts } from '@uirouter/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

const RULES = {
  DEFAULT: {
    redirectTo: `dice.${Project.BUDA}`,
  },
  STATES: {
    home: {
      matchTo: '/home',
      redirectTo: `dice.${Project.CONFIGURATION}.configurations`,
    },
    buda: {
      matchTo: '/buda',
      redirectTo: `dice.${Project.BUDA}`,
    },
    campaignManagement: {
      matchTo: '/oct',
      redirectTo: `dice.${Project.OCT}`,
    },
    transfertooladmin: {
      matchTo: '/transfertool-admin',
      redirectTo: `dice.${Project.TRANSFER_TOOL_ADMIN}`,
    },
    querytool: {
      matchTo: '/querytool',
      redirectTo: `dice.${Project.TRANSFER_TOOL_ADMIN}.overview`,
    },
    agentview: {
      matchTo: '/agent-view',
      redirectTo: `dice.${Project.AGENT_VIEW}`,
    },
    eima: {
      matchTo: '/eima',
      redirectTo: `dice.${Project.EIMA}`,
    },
  },
};

function getRegExp(state: string): RegExp {
  const pattern = `\\${state}(/([A-Za-z])*)*`;
  return new RegExp(pattern);
}

function findPredicate(ruleProperty: string, enteredPath: string): boolean {
  const found = enteredPath.match(getRegExp(RULES.STATES[ruleProperty].matchTo));
  return found && found.length > 0;
}

export function otherwiseRuleHandler(matchValue: boolean, urlParts: UrlParts, router: UIRouter): void {
  const redirectRuleProperty = Object.keys(RULES.STATES).find((property: string) =>
    findPredicate(property, urlParts.path),
  );
  const { redirectTo } = redirectRuleProperty ? RULES.STATES[redirectRuleProperty] : RULES.DEFAULT;

  router.stateService.go(redirectTo, {});
}

function initRouterTrace(trace: Trace): void {
  // trace.enable();
  //trace.enable(Category.TRANSITION);
}

function saveState(transition: Transition, injector: Injector, failedStateChange?: boolean): void {
  const paramsPathName: string = failedStateChange ? 'from' : 'to';
  const params = transition.params(paramsPathName);

  const to: StateObject = transition.$to();
  const from: StateObject = transition.$from();
  const currentState: StateObject = failedStateChange ? from : to;
  const path = currentState.name.split('.');

  const nav: NavigationElement = {
    path: path[2],
    additionalPath: path[3],
    project: path[1],
    param: params,
  };

  const storeService = injector.get(StoreService);
  storeService.save(Module.state, nav);
}

function saveInNGXSStore(transition: Transition, stateChangeType: StateChangeType): Observable<void> {
  const failedStateChange: boolean = stateChangeType === StateChangeType.ERROR;
  const paramsPathName: string = failedStateChange ? 'from' : 'to';

  const params = transition.params(paramsPathName);
  const to: StateObject = transition.$to();
  const from: StateObject = transition.$from();
  const currentState: StateObject = failedStateChange ? from : to;
  const previousState: StateObject = failedStateChange ? to : from;

  const store: Store = transition.injector().get(Store);
  const event =
    stateChangeType === StateChangeType.SUCCESS
      ? new RouterActions.SetCurrentState(currentState, previousState, params)
      : new RouterActions.SetCreatedState(currentState, previousState, params);
  return store.dispatch(event);
}

function requiresAuthHook(transitionService: TransitionService): void {
  transitionService.onBefore(
    {
      to: (state: StateObject) => {
        return state.data && !state.data.noAuthRequired;
      },
    },
    (transition: Transition) => {
      const aclService = transition.injector().get(AclService);
      if (!aclService.userExists()) {
        const authService = AppModuleInjector.get(AuthService);
        authService.logout();
      }
    },
    { priority: 10 },
  );
}

function initRouterTransitions(transitionService: TransitionService): void {
  const loaderHelpers: LoaderHelper[] = [];

  transitionService.onCreate({ to: 'dice.**' }, (transition: Transition) => {
    saveState(transition, transition.injector());

    const eventBusService = transition.injector().get(EventBusService);
    eventBusService.$emit(DIALOG.CLOSE);
  });

  transitionService.onCreate({}, (transition: Transition) => {
    saveInNGXSStore(transition, StateChangeType.CREATE).pipe(take(1)).subscribe();
    const loaderService: LoaderService = transition.injector().get(LoaderService);
    loaderHelpers.push(loaderService.show());
  });

  transitionService.onSuccess({}, (transition: Transition) => {
    if (loaderHelpers.length > 0) {
      loaderHelpers.pop().hide();
    }
    saveInNGXSStore(transition, StateChangeType.SUCCESS).pipe(take(1)).subscribe();
  });

  /**
   * When navigating within the application to a unauthorized location a dialog is shown, and the user is
   * navigated back to his previous state.
   */
  transitionService.onError(
    {
      from: (state, transition) => {
        return new RegExp(/dice(?=(?!\.login))(?=(\.(?:\.[^.]*)*))/g).test(state.name);
      },
    },
    (transition: Transition) => {
      const { detail } = transition.error();
      if (detail && detail.message === UNAUTHORIZED) {
        const aclService = transition.injector().get(AclService);
        if (!aclService.userExists()) {
          const authService = AppModuleInjector.get(AuthService);
          authService.logout();
        } else {
          const translate = transition.injector().get(TranslateService);
          const msg = translate.instant('dialog.message.no_permissions');

          const data: MessageDialogPayload = {
            type: MESSAGETYPE.INFORMATION,
            title: msg,
            disableCloseOnNavigation: true,
          };

          const dialogService = transition.injector().get(DialogService);
          dialogService.showMessageDialog(() => {
            const params = transition.params('from');
            const from: StateObject = transition.$from();
            transition.router.stateService.go(from.name, params);
          }, data);
        }
      }
    },
  );

  /**
   * When navigating within the application to a unauthorized location a dialog is shown, and the user is
   * navigated back to his previous state.
   */
  transitionService.onError(
    {
      from: (state, transition) => {
        const { name } = state;
        return name === '' || new RegExp(/dice\.login(?=(\.(?:\.[^.]*)*))*/g).test(state.name);
      },
    },
    (transition: Transition) => {
      const { detail } = transition.error();
      if (detail && detail.message === UNAUTHORIZED) {
        const aclService = transition.injector().get(AclService);
        if (aclService.userExists()) {
          aclService.navigateToPermittedState();
        } else {
          const authService = AppModuleInjector.get(AuthService);
          authService.logout();
        }
      }
    },
  );

  transitionService.onError({}, (transition: Transition) => {
    saveState(transition, transition.injector(), true);
    saveInNGXSStore(transition, StateChangeType.ERROR).pipe(take(1)).subscribe();

    if (loaderHelpers.length > 0) {
      loaderHelpers.pop().hide();
    }

    // alert(detail);
    // const regExp = new RegExp('Loading chunk ? failed');
    // const found = detail.match(regExp);
    // if (found && found.length > 0) {
    //     alert('REFRESH');
    // }
  });
}

export function uiRouterConfigFn(uiRouter: UIRouter, injector: Injector): void {
  const { transitionService, trace } = uiRouter;

  requiresAuthHook(transitionService);
  initRouterTransitions(transitionService);
  initRouterTrace(trace);
}
